import axios from 'axios';

// Function to get IP address from external service
async function getPublicIP() {
   try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      return data.ip;
   } catch (error) {
      console.error('Error getting IP:', error);
      return null;
   }
}

// Function to get location data
async function getLocationData() {
   return new Promise((resolve) => {
      if (navigator.geolocation) {
         navigator.geolocation.getCurrentPosition(
            (position) => {
               resolve({
                  latitude: position.coords.latitude,
                  longitude: position.coords.longitude,
                  accuracy: position.coords.accuracy,
                  timestamp: new Date().toISOString(),
               });
            },
            (error) => {
               console.error('Error getting location:', error);
               resolve(null);
            },
            {
               enableHighAccuracy: true,
               timeout: 10000,
               maximumAge: 600000, // 10 minutes
            },
         );
      } else {
         resolve(null);
      }
   });
}

// Function to get system information
function getSystemInfo() {
   const canvas = document.createElement('canvas');
   const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

   return {
      platform: navigator.platform,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      hardwareConcurrency: navigator.hardwareConcurrency,
      maxTouchPoints: navigator.maxTouchPoints,
      vendor: navigator.vendor,
      vendorSub: navigator.vendorSub,
      productSub: navigator.productSub,
      webdriver: navigator.webdriver,
      gpu: gl ? gl.getParameter(gl.renderer) : 'Unknown',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezoneOffset: new Date().getTimezoneOffset(),
      // colorDepth: screen.colorDepth,
      // pixelDepth: screen.pixelDepth,
      // availWidth: screen.availWidth,
      // availHeight: screen.availHeight,
      devicePixelRatio: window.devicePixelRatio,
      localStorage: typeof Storage !== 'undefined',
      sessionStorage: typeof Storage !== 'undefined',
      indexedDB: typeof indexedDB !== 'undefined',
      webSQLDatabase: typeof openDatabase !== 'undefined',
      plugins: Array.from(navigator.plugins).map((plugin) => ({
         name: plugin.name,
         filename: plugin.filename,
      })),
   };
}

// Enhanced function to gather all device information
async function getDeviceInfo() {
   const userAgent = navigator.userAgent;
   const browser = getBrowser(userAgent);
   const deviceName = getDeviceName(userAgent);
   const screenWidth = window.screen.width;
   const screenHeight = window.screen.height;
   const language = navigator.language || navigator.userLanguage;

   // Get IP address and location (these are async)
   const [publicIP, locationData] = await Promise.all([getPublicIP(), getLocationData()]);

   const systemInfo = getSystemInfo();

   return {
      browser,
      deviceName,
      screenWidth,
      screenHeight,
      language,
      publicIP,
      locationData,
      systemInfo,
      userAgent,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      referrer: document.referrer,
      onlineStatus: navigator.onLine,
   };
}

// Function to detect browser (enhanced)
function getBrowser(userAgent) {
   if (userAgent.indexOf('Edg') > -1) return 'Microsoft Edge';
   if (userAgent.indexOf('Chrome') > -1 && userAgent.indexOf('Edg') === -1) return 'Chrome';
   if (userAgent.indexOf('Safari') > -1 && userAgent.indexOf('Chrome') === -1) return 'Safari';
   if (userAgent.indexOf('Firefox') > -1) return 'Firefox';
   if (userAgent.indexOf('Opera') > -1 || userAgent.indexOf('OPR') > -1) return 'Opera';
   if (userAgent.indexOf('Trident') > -1) return 'Internet Explorer';
   return 'Unknown Browser';
}

// Enhanced function to detect device name
function getDeviceName(userAgent) {
   if (/iPhone/i.test(userAgent)) return 'iPhone';
   if (/iPad/i.test(userAgent)) return 'iPad';
   if (/iPod/i.test(userAgent)) return 'iPod';
   if (/Android/i.test(userAgent)) return 'Android Device';
   if (/BlackBerry/i.test(userAgent)) return 'BlackBerry';
   if (/Windows Phone/i.test(userAgent)) return 'Windows Phone';
   if (/Macintosh/i.test(userAgent)) return 'Mac';
   if (/Windows/i.test(userAgent)) return 'Windows PC';
   if (/Linux/i.test(userAgent)) return 'Linux';
   if (/Mobi/i.test(userAgent)) return 'Mobile Device';
   return 'Desktop Device';
}

// Cache for device info to avoid repeated calls
let deviceInfoCache = null;
let deviceInfoPromise = null;

// Function to get cached device info
async function getCachedDeviceInfo() {
   if (deviceInfoCache) {
      return deviceInfoCache;
   }

   if (deviceInfoPromise) {
      return deviceInfoPromise;
   }

   deviceInfoPromise = getDeviceInfo();
   deviceInfoCache = await deviceInfoPromise;
   deviceInfoPromise = null;

   return deviceInfoCache;
}

const axiosInstance = axios.create({
   baseURL: process.env.REACT_APP_BASEURL,
   headers: {
      'Content-Type': 'application/json',
      // 'X-Frontend-URL': frontendUrl,
   },
});

axiosInstance.interceptors.request.use(
   async (config) => {
      // Default Token
      // Get all the device info
      try {
         // Get all the device info (cached for performance)
         const deviceInfo = await getCachedDeviceInfo();

         // Add comprehensive device headers
         config.headers['X-Device-Browser'] = deviceInfo.browser;
         config.headers['X-Device-Name'] = deviceInfo.deviceName;
         config.headers['X-Device-Screen-Width'] = deviceInfo.screenWidth;
         config.headers['X-Device-Screen-Height'] = deviceInfo.screenHeight;
         config.headers['X-Device-Language'] = deviceInfo.language;
         config.headers['X-Device-User-Agent'] = deviceInfo.userAgent;
         config.headers['X-Device-Platform'] = `website - system:${deviceInfo.systemInfo.platform};`;
         config.headers['X-Device-Timezone'] = deviceInfo.systemInfo.timezone;
         config.headers['X-Device-Online-Status'] = deviceInfo.onlineStatus;
         config.headers['X-Device-Pixel-Ratio'] = deviceInfo.systemInfo.devicePixelRatio;
         config.headers['X-Device-Color-Depth'] = deviceInfo.systemInfo.colorDepth;
         config.headers['X-Device-Hardware-Concurrency'] = deviceInfo.systemInfo.hardwareConcurrency;
         config.headers['X-Device-Max-Touch-Points'] = deviceInfo.systemInfo.maxTouchPoints;
         config.headers['X-Device-Cookie-Enabled'] = deviceInfo.systemInfo.cookieEnabled;
         config.headers['X-Device-Do-Not-Track'] = deviceInfo.systemInfo.doNotTrack;
         config.headers['X-Device-GPU'] = deviceInfo.systemInfo.gpu;
         config.headers['X-Current-URL'] = deviceInfo.url;
         config.headers['X-Referrer'] = deviceInfo.referrer;
         config.headers['X-Request-Timestamp'] = new Date().toISOString();

         // Add IP and location if available
         if (deviceInfo.publicIP) {
            config.headers['X-Public-IP'] = deviceInfo.publicIP;
         }

         if (deviceInfo.locationData) {
            config.headers['X-Location-Latitude'] = deviceInfo.locationData.latitude;
            config.headers['X-Location-Longitude'] = deviceInfo.locationData.longitude;
            config.headers['X-Location-Accuracy'] = deviceInfo.locationData.accuracy;
            config.headers['X-Location-Timestamp'] = deviceInfo.locationData.timestamp;
         }

         // Add system capabilities
         config.headers['X-Storage-LocalStorage'] = deviceInfo.systemInfo.localStorage;
         config.headers['X-Storage-SessionStorage'] = deviceInfo.systemInfo.sessionStorage;
         config.headers['X-Storage-IndexedDB'] = deviceInfo.systemInfo.indexedDB;

         // Add plugins info (first 5 plugins to avoid header size issues)
         const topPlugins = deviceInfo.systemInfo.plugins.slice(0, 5);
         config.headers['X-Browser-Plugins'] = JSON.stringify(topPlugins);
      } catch (error) {
         console.error('Error gathering device info:', error);
      }

      //if (["admin/login", "country/all"].includes(config.url)) {
      if (['/user/login'].includes(config.url)) {
         // Add custom headers
         // config.headers['X-Browser'] = deviceInfo.browser;
         // config.headers['X-Device-Name'] = deviceInfo.deviceName;
         // config.headers['X-Screen-Width'] = deviceInfo.screenWidth;
         // config.headers['X-Screen-Height'] = deviceInfo.screenHeight;
         // config.headers['X-Language'] = deviceInfo.language;
         // config.headers["x-access-token"] = "essential";
         config.headers['Authorization'] = 'Bearer essential';
      } else if (['refresh/access'].includes(config.url)) {
         // config.headers["x-access-token"] = "refresh";
         config.headers['Authorization'] = 'refresh';
      } else {
         const accessToken = JSON.parse(localStorage.getItem('authTokenCustomer'))?.access_token;
         if (accessToken) {
            config.headers['Authorization'] = `Bearer ${accessToken}`;
            // config.headers["x-access-token"] = accessToken;
         }
      }
      return config;
   },
   (error) => {
      return Promise.reject(error);
   },
);

axiosInstance.interceptors.response.use(
   (response) => {
      return response;
   },
   async (error) => {
      const originalRequest = error.config;

      // If refresh token is expired, log the user out
      if (error.response.status === 401 && originalRequest.url === `/refresh/access`) {
         localStorage.removeItem('authTokenCustomer');
         window.location.reload();
         return Promise.reject(error);
      }

      // Access Token was expired
      if (error.response.status === 401 && !originalRequest._retry && originalRequest.url !== `/user/login`) {
         originalRequest._retry = true;
         try {
            const refreshToken = JSON.parse(localStorage.getItem('authTokenCustomer'))?.refresh_token;
            const res = await axiosInstance.post(`/refresh/access`, {
               token: refreshToken,
            });

            let auth = JSON.parse(localStorage.getItem('authTokenCustomer'));
            auth = { ...auth, access_token: res.data.access_token };
            localStorage.setItem('authTokenCustomer', JSON.stringify(auth));
            axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`;

            return axiosInstance(originalRequest);
         } catch (error) {
            return Promise.reject(error);
         }
      }
      return Promise.reject(error);
   },
);

export default axiosInstance;
